import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Terms() {
  return (
    <div className="min-h-screen bg-[#0a0a0f]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0f]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-lg shadow-lg shadow-violet-500/25">
              📊
            </div>
            <span className="text-xl font-bold text-white">WatchTheSize</span>
          </Link>
          
          <div className="flex items-center gap-6 text-sm text-zinc-400">
            <Link to="/terms" className="text-white font-medium">Terms</Link>
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
            <Link to="/refund" className="hover:text-white transition-colors">Refund</Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-3xl mx-auto">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Terms of Service
          </h1>
          
          <p className="text-zinc-400 mb-12">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>

          <div className="prose prose-invert prose-zinc max-w-none">
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">1. Agreement to Terms</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                By accessing or using WatchTheSize ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                These Terms apply to all visitors, users, and others who access or use the Service. By using the Service, you represent that you are at least 18 years of age, or that you are the age of majority in your jurisdiction, whichever is greater.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">2. Description of Service</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                WatchTheSize provides disk space monitoring software and related services for Linux servers. Our service includes:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>A lightweight monitoring agent installed on your servers</li>
                <li>A web-based dashboard for viewing disk usage statistics</li>
                <li>Email and webhook notifications for disk usage alerts</li>
                <li>AI-powered insights and recommendations (for applicable plans)</li>
                <li>API access for integration with other tools (for applicable plans)</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">3. User Accounts</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                When you create an account with us, you must provide accurate, complete, and current information. Failure to do so constitutes a breach of the Terms, which may result in immediate termination of your account.
              </p>
              <p className="text-zinc-400 leading-relaxed mb-4">
                You are responsible for:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Safeguarding the password you use to access the Service</li>
                <li>Any activities or actions under your account</li>
                <li>Notifying us immediately of any unauthorized use of your account</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">4. Subscriptions and Payments</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Some parts of the Service are billed on a subscription basis. You will be billed in advance on a recurring basis ("Billing Cycle"). Billing cycles are set on a monthly basis.
              </p>
              <p className="text-zinc-400 leading-relaxed mb-4">
                A valid payment method is required to process the payment for your subscription. You shall provide accurate and complete billing information.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                We reserve the right to modify subscription fees upon 30 days' notice. Such notice may be provided at any time by posting the changes to the WatchTheSize website or via email.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">5. Free Trial</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                WatchTheSize may offer a free trial for a limited period of time ("Free Trial"). You may be required to enter billing information to sign up for the Free Trial.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                At the end of the Free Trial period, your subscription will begin unless you cancel before the trial ends. You may cancel at any time without charge during the Free Trial.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">6. Acceptable Use</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                You agree not to use the Service to:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Transmit any malicious code or attempt to gain unauthorized access</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Use the Service for any purpose other than its intended use</li>
                <li>Resell, redistribute, or sublicense the Service without authorization</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">7. Data Collection and Privacy</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                Our monitoring agent collects disk usage data from your servers. This includes:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2 mb-4">
                <li>Total disk capacity and used space</li>
                <li>Directory sizes and file counts</li>
                <li>File names and paths (for top space consumers)</li>
                <li>System metadata (hostname, OS version)</li>
              </ul>
              <p className="text-zinc-400 leading-relaxed">
                We do not collect or access the contents of your files. For more information, please review our <Link to="/privacy" className="text-violet-400 hover:text-violet-300">Privacy Policy</Link>.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">8. Intellectual Property</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                The Service and its original content, features, and functionality are and will remain the exclusive property of WatchTheSize and its licensors. The Service is protected by copyright, trademark, and other laws.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                Our trademarks may not be used in connection with any product or service without our prior written consent.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">9. Limitation of Liability</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                In no event shall WatchTheSize, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
              </p>
              <ul className="list-disc list-inside text-zinc-400 space-y-2">
                <li>Your access to or use of or inability to access or use the Service</li>
                <li>Any conduct or content of any third party on the Service</li>
                <li>Any content obtained from the Service</li>
                <li>Unauthorized access, use, or alteration of your transmissions or content</li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">10. Disclaimer</h2>
              <p className="text-zinc-400 leading-relaxed">
                The Service is provided on an "AS IS" and "AS AVAILABLE" basis. WatchTheSize makes no warranties, expressed or implied, regarding the Service, including but not limited to implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">11. Termination</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service or contact us to delete your account.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">12. Changes to Terms</h2>
              <p className="text-zinc-400 leading-relaxed mb-4">
                We reserve the right to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect.
              </p>
              <p className="text-zinc-400 leading-relaxed">
                By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-bold text-white mb-4">13. Contact Us</h2>
              <p className="text-zinc-400 leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <p className="text-violet-400 mt-4">
                support@watchthesize.com
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-white/5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-fuchsia-500 text-sm">
                📊
              </div>
              <span className="text-sm font-semibold text-white">WatchTheSize</span>
            </Link>
            
            <div className="flex items-center gap-6 text-sm text-zinc-400">
              <Link to="/terms" className="text-white">Terms</Link>
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/refund" className="hover:text-white transition-colors">Refund</Link>
              <a href="#" className="hover:text-white transition-colors">Support</a>
            </div>
            
            <p className="text-sm text-zinc-500">
              © {new Date().getFullYear()} WatchTheSize. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

